import { FC, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import Select from "react-select";
import NotificationDispatch, { showErrorNotification, useNotification } from "../../context/notificationContext";
import { useI18n } from "../../hooks/useTranslations";
import { useAdminCarsApi } from "../../http/cars/api";
import {
  Car,
  CarCategory,
  FuelType,
  getCategories,
  getFuelTypes,
  getStatus,
  Status,
  TypeOfUse,
} from "../../http/cars/types";
import { useManufacturersApi } from "../../http/manufacturers/api";
import { Manufacturer } from "../../http/manufacturers/types";
import { Subsidiary } from "../../http/subsidiaries/types";
import DatePicker from "../../ui/DatePicker";
import Form from "../ui/Form";
import FormField from "../ui/FormField";
import FormGrid from "../ui/FormGrid";
import Input from "../ui/Input";
import ModalDialog from "../ui/ModalDialog";
import Notification from "../ui/Notification";
import Numeric from "../ui/Numeric";
import reactSelectStyles from "../ui/ReactSelectUtils";
import Switch from "../ui/Switch";
import Textarea from "../ui/Textarea";

const MANUFACTURER_FIELDS = ["id", "name"] as const;
type ManufacturerField = (typeof MANUFACTURER_FIELDS)[number];
type ManufacturerSearch = Pick<Manufacturer, ManufacturerField>;

export interface EditCarFormBody {
  vehicleIdentificationNumber: string;
  plateNumber?: string;
  typeOfUse?: TypeOfUse;
  notes?: string;
  manufacturer: ManufacturerSearch;
  model: string;
  hp?: number;
  displacement?: string;
  co2Emissions?: string;
  fuelType?: FuelType;
  category?: CarCategory;
  isPrivateCar?: boolean;
  status: Status;
  subsidiary: Subsidiary;
  registrationDate?: string;
  purchaseDate?: string;
  soldDate?: string;
}

type EditCarFormDialogProps = {
  car?: Car | null;
  onSubmit: () => void;
  isDisabled?: boolean;
  isLoading?: boolean;
  onClose: () => void;
};

const EditCarFormDialog: FC<EditCarFormDialogProps> = ({
  car,
  onClose,
  onSubmit,
  isDisabled,
  isLoading,
}: EditCarFormDialogProps) => {
  const i18n = useI18n();
  const manufacturerApi = useManufacturersApi();
  const aborter = useRef(new AbortController());
  const carsApi = useAdminCarsApi();
  const [{ show, severity, message, sticky }, dispatch] = useNotification();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const categories = getCategories(i18n.t);
  const status = getStatus(i18n.t);
  const fuelTypes = getFuelTypes(i18n.t);

  const manufacturers = manufacturerApi.useAdminManufacturers(
    { limit: 100, fields: MANUFACTURER_FIELDS },
    aborter.current.signal,
  );

  const defaultValues = {
    vehicleIdentificationNumber: car?.vehicleIdentificationNumber,
    notes: car?.notes,
    manufacturer: car?.manufacturer,
    model: car?.model,
    hp: car?.hp,
    displacement: car?.displacement,
    co2Emissions: car?.co2Emissions,
    fuelType: fuelTypes.find((x) => x.value === car?.fuelType),
    category: categories.find((x) => x.value === car?.category),
    isPrivateCar: car?.isPrivateCar,
    status: status.find((x) => x.value === car?.status),
    plateNumber: car?.currentPlateNumber,
    registrationDate: car?.registrationDate,
    purchaseDate: car?.purchaseDate,
    soldDate: car?.soldDate,
  };

  const onSubmitCar = async (form: EditCarFormBody) => {
    if (car?.id) {
      try {
        setIsSubmitting(true);
        await carsApi.updateCar(car.id, {
          vehicleIdentificationNumber: form.vehicleIdentificationNumber,
          registrationDate: form.registrationDate,
          notes: form.notes,
          manufacturerId: form.manufacturer.id,
          model: form.model,
          hp: form.hp!,
          displacement: form.displacement,
          co2Emissions: form.co2Emissions,
          fuelType: form.fuelType?.value!,
          category: form.category?.value,
          isPrivateCar: form.isPrivateCar ?? false,
          status: form.status.value,
          plateNumber: form.plateNumber,
          currentVersionNumber: car.versionNumber,
          purchaseDate: form.purchaseDate,
          soldDate: form.soldDate,
        });
        onSubmit();
      } catch (error) {
        dispatch(showErrorNotification(i18n, error));
        setIsSubmitting(false);
      }
    }
  };

  return (
    <ModalDialog title={i18n.t.cars.edit} onClose={() => onClose()}>
      <NotificationDispatch.Provider value={dispatch}>
        <Form<EditCarFormBody>
          defaultValues={defaultValues}
          submitText={i18n.t.common.save}
          disabled={isDisabled}
          onSubmit={() => onSubmitCar}
          itemCreationInfo={car}
        >
          {({ register, formState: { errors }, control, reset, setValue, watch }) => {
            useEffect(() => {
              reset(defaultValues);
            }, [car?.id]);

            const isPrivateCar = watch("isPrivateCar");

            return (
              <>
                <FormGrid columns={2}>
                  <FormField
                    label={i18n.t.common.vehicleIdentificationNumber}
                    error={errors.vehicleIdentificationNumber}
                  >
                    {({ labelId, isInvalid }) => (
                      <Input
                        id={labelId}
                        isInvalid={isInvalid}
                        disabled={isDisabled || isLoading || isSubmitting}
                        {...register("vehicleIdentificationNumber", {
                          required: true,
                          minLength: 1,
                        })}
                      />
                    )}
                  </FormField>
                  <Controller
                    name="registrationDate"
                    control={control}
                    rules={{
                      required: true,
                      validate: (value) => {
                        if (!value) return i18n.t.common.invalidDate;
                        const d = new Date(value);
                        if (d.getFullYear() < 1000) return i18n.t.common.invalidDate;
                        return undefined;
                      },
                    }}
                    render={({ fieldState }) => (
                      <FormField label={i18n.t.common.registrationDate} error={fieldState.error}>
                        {({ isInvalid }) => (
                          <DatePicker
                            onChange={(v) => setValue("registrationDate", v!)}
                            isInvalid={isInvalid}
                            value={watch("registrationDate")}
                            disabled={isDisabled || isLoading}
                          />
                        )}
                      </FormField>
                    )}
                  />

                  <Controller
                    name="purchaseDate"
                    control={control}
                    rules={{
                      validate: (value) => {
                        if (value) {
                          if (new Date(value).getFullYear() < 1000) {
                            return i18n.t.common.invalidDate;
                          }
                          const soldDate = watch("soldDate");
                          if (soldDate && value > soldDate) {
                            return i18n.t.carOrders.errors.carOrderDateAfterSoldDate;
                          }
                        }
                        return undefined;
                      },
                    }}
                    render={({ fieldState }) => (
                      <FormField label={i18n.t.common.purchaseDate} error={fieldState.error} isOptional>
                        {({ isInvalid }) => (
                          <DatePicker
                            onChange={(v) => setValue("purchaseDate", v!)}
                            isInvalid={isInvalid}
                            value={watch("purchaseDate")}
                            disabled={isDisabled || isLoading}
                          />
                        )}
                      </FormField>
                    )}
                  />

                  <Controller
                    name="soldDate"
                    control={control}
                    rules={{
                      validate: (value) => {
                        if (value && new Date(value).getFullYear() < 1000) {
                          return i18n.t.common.invalidDate;
                        }
                        return undefined;
                      },
                    }}
                    render={({ fieldState }) => (
                      <FormField label={i18n.t.common.soldDate} error={fieldState.error} isOptional>
                        {({ isInvalid }) => (
                          <DatePicker
                            onChange={(v) => setValue("soldDate", v!)}
                            isInvalid={isInvalid}
                            value={watch("soldDate")}
                            disabled={isDisabled || isLoading}
                          />
                        )}
                      </FormField>
                    )}
                  />

                  {/* If the subsidiary does not allow multiple plates, show the disabled field. */}
                  <FormField
                    label={i18n.t.plateNumbers.singular}
                    error={errors.plateNumber}
                    hidden={!!car && !!car.carSubsidiary && !!car.carSubsidiary?.subsidiary?.allowMultiPlates}
                  >
                    {({ labelId, isInvalid }) => (
                      <Input
                        id={labelId}
                        isInvalid={isInvalid}
                        disabled={
                          isDisabled ||
                          isLoading ||
                          isSubmitting ||
                          !car ||
                          (!!car && car.carSubsidiary?.subsidiary?.allowMultiPlates)
                        }
                        {...register("plateNumber", {
                          required: !!car && car.carSubsidiary?.subsidiary?.allowMultiPlates,
                        })}
                      />
                    )}
                  </FormField>
                  <FormField
                    label={i18n.t.plateNumbers.singular}
                    error={errors.plateNumber}
                    hidden={!car || (!!car && !car.carSubsidiary?.subsidiary?.allowMultiPlates)}
                  >
                    {({ labelId }) => (
                      <Input id={labelId} disabled value={car?.currentPlateNumber ?? car?.lastPlateNumber ?? ""} />
                    )}
                  </FormField>

                  <Controller
                    name="manufacturer"
                    control={control}
                    rules={{ required: true }}
                    render={({ field, fieldState }) => (
                      <FormField label={i18n.t.manufacturers.singular} error={fieldState.error}>
                        {({ labelId, isInvalid }) => (
                          <Select
                            menuPosition="fixed"
                            {...field}
                            inputId={labelId}
                            options={manufacturers.data?.items}
                            placeholder={`${i18n.t.common.select}...`}
                            isDisabled={isDisabled || isLoading || isSubmitting}
                            getOptionLabel={(x) => x.name}
                            getOptionValue={(x) => x.id}
                            defaultInputValue={car?.manufacturer.name}
                            styles={reactSelectStyles<ManufacturerSearch>(isInvalid)}
                          />
                        )}
                      </FormField>
                    )}
                  />
                  <FormField label={i18n.t.common.model} error={errors.model}>
                    {({ labelId, isInvalid }) => (
                      <Input
                        id={labelId}
                        isInvalid={isInvalid}
                        disabled={isDisabled || isLoading || isSubmitting}
                        {...register("model", {
                          required: true,
                          minLength: 1,
                        })}
                      />
                    )}
                  </FormField>

                  <FormField label={i18n.t.common.engineCapacity} error={errors.displacement} isOptional>
                    {({ labelId, isInvalid, isOptional }) => (
                      <Input
                        id={labelId}
                        isInvalid={isInvalid}
                        disabled={isDisabled || isLoading || isSubmitting}
                        {...register("displacement", {
                          required: !isOptional,
                          minLength: 1,
                        })}
                      />
                    )}
                  </FormField>

                  <Controller
                    name="hp"
                    control={control}
                    render={({ field, fieldState }) => (
                      <FormField label={i18n.t.common.hp} error={fieldState.error} isOptional>
                        {({ labelId, isInvalid }) => (
                          <Numeric
                            {...field}
                            id={labelId}
                            isInvalid={isInvalid}
                            disabled={isDisabled || isLoading || isSubmitting}
                            onBlur={(value) => setValue("hp", value ?? undefined)}
                            decimalScale={0}
                            allowNegative={false}
                          />
                        )}
                      </FormField>
                    )}
                  />

                  <Controller
                    name="fuelType"
                    control={control}
                    render={({ field, fieldState }) => (
                      <FormField label={i18n.t.common.fuelType} error={fieldState.error} isOptional>
                        {({ labelId, isInvalid }) => (
                          <Select
                            menuPosition="fixed"
                            {...field}
                            inputId={labelId}
                            options={fuelTypes}
                            placeholder={`${i18n.t.common.select}...`}
                            isDisabled={isDisabled || isLoading || isSubmitting}
                            getOptionLabel={(x) => x.label}
                            getOptionValue={(x) => x.value}
                            styles={reactSelectStyles<FuelType>(isInvalid)}
                          />
                        )}
                      </FormField>
                    )}
                  />
                  <Controller
                    name="category"
                    control={control}
                    render={({ field, fieldState }) => (
                      <FormField label={i18n.t.common.category} error={fieldState.error} isOptional>
                        {({ labelId, isInvalid }) => (
                          <Select
                            menuPosition="fixed"
                            {...field}
                            inputId={labelId}
                            options={categories}
                            isClearable
                            placeholder={`${i18n.t.common.select}...`}
                            isDisabled={isDisabled || isLoading || isSubmitting}
                            getOptionLabel={(x) => x.label}
                            getOptionValue={(x) => x.value}
                            styles={reactSelectStyles<CarCategory>(isInvalid)}
                          />
                        )}
                      </FormField>
                    )}
                  />
                  <FormField label={i18n.t.common.privateCar} error={errors.isPrivateCar} isOptional>
                    {({ labelId }) => (
                      <Switch
                        id={labelId}
                        checked={isPrivateCar ?? false}
                        onChange={(checked) => setValue("isPrivateCar", checked)}
                        disabled={isDisabled}
                      />
                    )}
                  </FormField>
                  <Controller
                    name="status"
                    control={control}
                    rules={{ required: true }}
                    render={({ field, fieldState }) => (
                      <FormField label={i18n.t.common.state} error={fieldState.error}>
                        {({ labelId, isInvalid }) => (
                          <Select
                            menuPosition="fixed"
                            {...field}
                            inputId={labelId}
                            options={status}
                            placeholder={`${i18n.t.common.select}...`}
                            isDisabled={isDisabled || isLoading || isSubmitting}
                            getOptionLabel={(x) => x.label}
                            getOptionValue={(x) => x.value}
                            styles={reactSelectStyles<Status>(isInvalid)}
                          />
                        )}
                      </FormField>
                    )}
                  />
                  <FormField label={i18n.t.common.co2} error={errors.notes} isOptional>
                    {({ labelId, isInvalid, isOptional }) => (
                      <Input
                        id={labelId}
                        isInvalid={isInvalid}
                        disabled={isDisabled || isLoading || isSubmitting}
                        {...register("co2Emissions", {
                          required: !isOptional,
                          minLength: 1,
                        })}
                      />
                    )}
                  </FormField>

                  <FormField label={i18n.t.common.notes} error={errors.notes} isOptional fullWidth>
                    {({ labelId, isOptional }) => (
                      <Textarea
                        id={labelId}
                        disabled={isDisabled || isLoading || isSubmitting}
                        {...register("notes", {
                          required: !isOptional,
                          minLength: 1,
                        })}
                      />
                    )}
                  </FormField>
                </FormGrid>
              </>
            );
          }}
        </Form>
        {show && <Notification type={severity} text={message} sticky={sticky} />}
      </NotificationDispatch.Provider>
    </ModalDialog>
  );
};

export default EditCarFormDialog;
